<template>
  <v-container fluid>
    <v-card>
      <v-card-title>Gestión de inventario</v-card-title>
      <v-card-text>
        <v-row dense>
          <v-col cols="12" md="4">
            <v-select
              v-model="movementType"
              :items="movementTypeOptions"
              item-text="label"
              item-value="value"
              label="Tipo de movimiento"
              outlined
              dense
            ></v-select>
          </v-col>
          <v-col cols="12" md="4" v-if="isTransfer">
            <v-select
              v-model="sourceLocationId"
              :items="locations"
              item-text="NAME"
              item-value="ID"
              label="Almacén origen"
              outlined
              dense
            ></v-select>
          </v-col>
          <v-col cols="12" md="4" v-if="isTransfer">
            <v-select
              v-model="destinationLocationId"
              :items="destinationLocationOptions"
              item-text="NAME"
              item-value="ID"
              label="Almacén destino"
              outlined
              dense
            ></v-select>
          </v-col>
          <v-col cols="12" md="4" v-else>
            <v-select
              v-model="locationId"
              :items="locations"
              item-text="NAME"
              item-value="ID"
              label="Almacén"
              outlined
              dense
            ></v-select>
          </v-col>
        </v-row>

        <v-divider class="my-4"></v-divider>

        <v-row dense>
          <v-col cols="12">
            <v-autocomplete
              v-model="selectedProductToAdd"
              :search-input.sync="productSearchText"
              :items="searchedProducts"
              item-text="NAME"
              item-value="ID"
              label="Buscar producto por nombre, SKU o código de barras"
              outlined
              dense
              clearable
              return-object
              :loading="loadingSearchProducts"
              :disabled="!effectiveLocationId"
              no-filter
              hide-no-data
              @change="onProductSelected"
            >
              <template #[`item`]="{ item }">
                <div class="py-1">
                  <div class="font-weight-medium">{{ item.NAME }}</div>
                  <div class="caption grey--text">
                    Código: {{ item.CODE || '-' }} | SKU: {{ item.REFERENCE || '-' }} | Stock: {{ item.stock }}
                  </div>
                </div>
              </template>
              <template #[`selection`]="{ item }">
                <span>{{ item.NAME }}</span>
              </template>
            </v-autocomplete>
            <div class="caption grey--text mt-1">
              Escribe al menos 3 caracteres para buscar. El resultado se carga al dejar de escribir.
            </div>
          </v-col>
        </v-row>

        <v-divider class="my-6"></v-divider>

        <div class="subtitle-1 font-weight-medium mb-2">Productos a mover</div>
        <v-data-table
          :headers="movementHeaders"
          :items="itemsToMove"
          no-data-text="Agrega productos a la lista para registrar el movimiento"
          class="elevation-1"
          dense
        >
          <template #[`item.currentStock`]="{ item }">
            <v-chip :color="getStockColor(item.currentStock)" dark small>
              {{ item.currentStock }}
            </v-chip>
          </template>
          <template #[`item.quantity`]="{ item }">
            <v-text-field
              v-model.number="item.quantity"
              type="number"
              min="0.001"
              step="0.001"
              hide-details
              outlined
              dense
              class="qty-input"
            ></v-text-field>
          </template>
          <template #[`item.actions`]="{ item }">
            <v-btn icon small color="red" @click="removeItem(item)">
              <v-icon small>mdi-delete</v-icon>
            </v-btn>
          </template>
        </v-data-table>

        <div class="d-flex justify-end mt-4 mb-2">
          <v-btn color="primary" :loading="savingMovement" @click="saveMovement">
            Registrar movimiento
          </v-btn>
        </div>

        <div class="d-none d-md-block">
          <v-divider class="my-6"></v-divider>

          <div class="subtitle-1 font-weight-medium mb-2">Catálogo por categoría</div>
          <v-slide-group
            v-model="selectedCatalogCategoryName"
            show-arrows
            class="mb-4"
          >
            <v-slide-item value="">
              <v-chip class="mr-2" :input-value="selectedCatalogCategoryName === ''" @click="selectedCatalogCategoryName = ''">
                Todas
              </v-chip>
            </v-slide-item>
            <v-slide-item
              v-for="category in categories"
              :key="category.ID"
              :value="category.NAME"
            >
              <v-chip
                class="mr-2"
                :input-value="selectedCatalogCategoryName === category.NAME"
                @click="selectedCatalogCategoryName = category.NAME"
              >
                {{ category.NAME }}
              </v-chip>
            </v-slide-item>
          </v-slide-group>

          <v-row dense class="catalog-scroll-area">
            <v-col
              v-for="product in categorizedCatalogProducts"
              :key="`cat-${product.ID}`"
              cols="12"
              sm="6"
              md="4"
              lg="3"
            >
              <v-card outlined class="pa-2">
                <div class="font-weight-medium text-truncate">{{ product.NAME }}</div>
                <div class="caption grey--text">Código: {{ product.CODE || '-' }}</div>
                <div class="caption grey--text">SKU: {{ product.REFERENCE || '-' }}</div>
                <div class="caption grey--text">Existencia: {{ product.stock }}</div>
                <div class="caption grey--text">Compra: ${{ Number(product.PRICEBUY || 0).toFixed(2) }}</div>
                <v-card-actions class="px-0 pb-0">
                  <v-btn small text color="primary" @click="addProductToMove(product)">Agregar</v-btn>
                </v-card-actions>
              </v-card>
            </v-col>
          </v-row>
        </div>
      </v-card-text>
    </v-card>
  </v-container>
</template>

<script>
import * as locationService from '@/services/locationService';
import * as categoryService from '@/services/categoryService';
import * as productService from '@/services/productService';
import * as inventoryService from '@/services/inventoryService';
import { stockMovementLabel } from '@/utils/legacyPosLabels';

export default {
  name: 'InventoryManagementView',
  data() {
    return {
      movementType: 'entry_purchase',
      movementTypeOptions: [
        { value: 'entry_purchase', label: stockMovementLabel(1) },
        { value: 'entry_return', label: stockMovementLabel(2) },
        { value: 'output_sale', label: stockMovementLabel(-1) },
        { value: 'output_return', label: stockMovementLabel(-2) },
        { value: 'transfer', label: stockMovementLabel(1000) },
      ],

      locations: [],
      categories: [],
      locationId: null,
      sourceLocationId: null,
      destinationLocationId: null,

      productSearchText: '',
      selectedProductToAdd: null,
      searchedProducts: [],
      loadingSearchProducts: false,
      selectedCatalogCategoryName: '',
      searchDebounce: null,

      loadingProducts: false,
      savingMovement: false,
      catalogProducts: [],
      itemsToMove: [],

      movementHeaders: [
        { text: 'Código de barras', value: 'CODE', sortable: false },
        { text: 'SKU', value: 'REFERENCE', sortable: false },
        { text: 'Nombre', value: 'NAME', sortable: false },
        { text: 'Existencia actual', value: 'currentStock', sortable: false },
        { text: 'Precio compra', value: 'PRICEBUY', sortable: false },
        { text: 'Cantidad a mover', value: 'quantity', sortable: false, width: '170px' },
        { text: '', value: 'actions', sortable: false, align: 'end', width: '48px' },
      ],
    };
  },
  computed: {
    isTransfer() {
      return this.movementType === 'transfer';
    },
    effectiveLocationId() {
      return this.isTransfer ? this.sourceLocationId : this.locationId;
    },
    destinationLocationOptions() {
      if (!this.sourceLocationId) return this.locations;
      return this.locations.filter(loc => loc.ID !== this.sourceLocationId);
    },
    categorizedCatalogProducts() {
      if (!this.selectedCatalogCategoryName) return this.catalogProducts;
      return this.catalogProducts.filter(
        p => p.category?.NAME === this.selectedCatalogCategoryName,
      );
    },
  },
  watch: {
    movementType() {
      this.itemsToMove = [];
      this.searchedProducts = [];
      this.productSearchText = '';
      this.selectedProductToAdd = null;
      if (!this.isTransfer) {
        this.destinationLocationId = null;
      }
      this.fetchCatalogProducts();
    },
    effectiveLocationId() {
      this.itemsToMove = [];
      this.searchedProducts = [];
      this.productSearchText = '';
      this.selectedProductToAdd = null;
      this.fetchCatalogProducts();
    },
    productSearchText() {
      this.debounceSearchProducts();
    },
  },
  async mounted() {
    await Promise.all([this.fetchLocations(), this.fetchCategories()]);
    this.fetchCatalogProducts();
  },
  methods: {
    debounceSearchProducts() {
      clearTimeout(this.searchDebounce);
      this.searchDebounce = setTimeout(() => this.searchProducts(), 400);
    },
    async fetchLocations() {
      try {
        this.locations = await locationService.fetchLocations();
        if (this.locations.length > 0) {
          this.locationId = this.locations[0].ID;
          this.sourceLocationId = this.locations[0].ID;
          if (this.locations.length > 1) {
            this.destinationLocationId = this.locations[1].ID;
          }
        }
      } catch (error) {
        this.$emit('show-alert', { message: 'Error al cargar almacenes.', type: 'error' });
      }
    },
    async fetchCategories() {
      try {
        this.categories = await categoryService.fetchCategories();
      } catch (error) {
        this.$emit('show-alert', { message: 'Error al cargar categorías.', type: 'error' });
      }
    },
    async searchProducts() {
      if (!this.effectiveLocationId) {
        this.searchedProducts = [];
        return;
      }
      const term = (this.productSearchText || '').trim();
      if (term.length < 3) {
        this.searchedProducts = [];
        return;
      }

      this.loadingSearchProducts = true;
      try {
        const data = await productService.fetchProducts({
          page: 1,
          itemsPerPage: 20,
          locationId: this.effectiveLocationId,
          q: term,
          sortBy: [],
          sortDesc: [],
        });
        this.searchedProducts = data.products;
      } catch (error) {
        this.searchedProducts = [];
        this.$emit('show-alert', { message: 'Error al buscar productos.', type: 'error' });
      } finally {
        this.loadingSearchProducts = false;
      }
    },
    async fetchCatalogProducts() {
      if (!this.effectiveLocationId) {
        this.catalogProducts = [];
        return;
      }
      this.loadingProducts = true;
      try {
        const data = await productService.fetchProducts({
          page: 1,
          itemsPerPage: 200,
          locationId: this.effectiveLocationId,
          sortBy: [],
          sortDesc: [],
        });
        this.catalogProducts = data.products;
      } catch (error) {
        this.catalogProducts = [];
        this.$emit('show-alert', { message: 'Error al cargar productos para inventario.', type: 'error' });
      } finally {
        this.loadingProducts = false;
      }
    },
    onProductSelected(product) {
      if (!product) return;
      this.addProductToMove(product);
      this.selectedProductToAdd = null;
      this.productSearchText = '';
      this.searchedProducts = [];
    },
    addProductToMove(product) {
      const existing = this.itemsToMove.find(item => item.ID === product.ID);
      if (existing) {
        existing.quantity = Number(existing.quantity || 0) + 1;
        return;
      }
      this.itemsToMove.push({
        ID: product.ID,
        CODE: product.CODE,
        REFERENCE: product.REFERENCE,
        NAME: product.NAME,
        currentStock: Number(product.stock || 0),
        PRICEBUY: Number(product.PRICEBUY || 0),
        quantity: 1,
      });
    },
    removeItem(item) {
      this.itemsToMove = this.itemsToMove.filter(i => i.ID !== item.ID);
    },
    getStockColor(stock) {
      const numeric = Number(stock || 0);
      if (numeric > 20) return 'green';
      if (numeric > 0) return 'orange';
      return 'red';
    },
    async saveMovement() {
      if (!this.effectiveLocationId) {
        this.$emit('show-alert', { message: 'Selecciona un almacén.', type: 'warning' });
        return;
      }
      if (this.isTransfer) {
        if (!this.destinationLocationId) {
          this.$emit('show-alert', { message: 'Selecciona un almacén destino.', type: 'warning' });
          return;
        }
        if (this.sourceLocationId === this.destinationLocationId) {
          this.$emit('show-alert', {
            message: 'Origen y destino no pueden ser el mismo almacén.',
            type: 'warning',
          });
          return;
        }
      }
      if (this.itemsToMove.length === 0) {
        this.$emit('show-alert', { message: 'Agrega al menos un producto.', type: 'warning' });
        return;
      }

      const invalidQty = this.itemsToMove.find(item => !Number(item.quantity) || Number(item.quantity) <= 0);
      if (invalidQty) {
        this.$emit('show-alert', { message: 'Todas las cantidades deben ser mayores a 0.', type: 'warning' });
        return;
      }

      this.savingMovement = true;
      try {
        await inventoryService.registerInventoryMovement({
          movementType: this.movementType,
          locationId: this.isTransfer ? null : this.locationId,
          sourceLocationId: this.isTransfer ? this.sourceLocationId : null,
          destinationLocationId: this.isTransfer ? this.destinationLocationId : null,
          items: this.itemsToMove.map(item => ({
            productId: item.ID,
            quantity: Number(item.quantity),
            price: Number(item.PRICEBUY || 0),
          })),
        });

        this.$emit('show-alert', { message: 'Movimiento registrado correctamente.', type: 'success' });
        this.itemsToMove = [];
        await this.fetchCatalogProducts();
      } catch (error) {
        this.$emit('show-alert', {
          message: `Error al registrar movimiento: ${error.response?.data?.mensaje || error.message}`,
          type: 'error',
        });
      } finally {
        this.savingMovement = false;
      }
    },
  },
};
</script>

<style scoped>
.qty-input {
  max-width: 150px;
}

.catalog-scroll-area {
  max-height: 420px;
  overflow-y: auto;
}
</style>
